import {
    AGENDA_CONTEXT_MAX_LENGTH,
    AGENDA_DEFAULT_TOPIC_DURATION_MINUTES,
    AGENDA_DISCUSSION_POINT_TITLE_MAX_LENGTH,
    AGENDA_ITEM_MAX_DURATION_MINUTES,
    AGENDA_ITEM_TITLE_MAX_LENGTH,
    AGENDA_MAX_DISCUSSION_POINTS_PER_ITEM,
    AGENDA_MAX_ITEMS,
    AGENDA_OPENING_STATEMENT_MAX_LENGTH,
} from './agendaLimits';

import type {
    LocalizedText,
    MeetingAgenda,
    MeetingAgendaUpdatePayload,
} from '@/types/review/PostReviewMeeting';
import type { ContentLanguage } from '@/utils/contentLanguage';

/**
 * The agenda is stored bilingually ({ en, fr }), but the builder and its child components work with plain
 * strings in the reader's language. These view types are that single-language projection; the builder
 * projects the saved agenda into them for display/editing and merges the edited language back on save
 * (keeping the other language untouched).
 */
export interface AgendaPointView {
    id: string;
    title: string;
    order: number;
    hasDetails: boolean;
    context: string | null;
    dateAdded: string;
    response: string | null;
    isAiGenerated: boolean;
}

export interface AgendaItemView {
    id: string;
    title: string;
    durationMinutes: number;
    order: number;
    hasHandout: boolean;
    isAiGenerated: boolean;
    discussionPoints: AgendaPointView[];
}

/** The reader's language value from a bilingual field, falling back to the other language. */
export const pickText = (
    text: LocalizedText | null | undefined,
    language: ContentLanguage,
): string => {
    if (!text) return '';
    return (text[language] ?? (language === 'en' ? text.fr : text.en) ?? '').trim();
};

/** Merge an edited (reader-language) value into a bilingual field, keeping the other language. */
const withLanguage = (
    original: LocalizedText | null | undefined,
    language: ContentLanguage,
    value: string,
): LocalizedText => ({ ...(original ?? {}), [language]: value });

/** Everything needed to merge edits back: the saved agenda's bilingual values keyed by element id. */
export interface AgendaBilingualSource {
    openingStatement: LocalizedText;
    itemTitles: Record<string, LocalizedText>;
    pointTitles: Record<string, LocalizedText>;
    pointContexts: Record<string, LocalizedText | null>;
}

/** An agenda in the reader's language, with the bilingual originals its edits are merged back into. */
export interface AgendaProjection {
    openingStatement: string;
    items: AgendaItemView[];
    source: AgendaBilingualSource;
}

/** The projection of an agenda never saved: nothing to show, nor any other language to preserve. */
export const EMPTY_AGENDA_PROJECTION: AgendaProjection = {
    openingStatement: '',
    items: [],
    source: { openingStatement: {}, itemTitles: {}, pointTitles: {}, pointContexts: {} },
};

/** Projects the bilingual agenda into the reader's language, and captures the originals for later merge.
 * Only reads `openingStatement`/`items`, so any agenda shape sharing those two fields works here —
 * notably `ThematicMeetingAgenda`, which omits the cycle-review-only `cycleInstanceId`. */
export const projectAgenda = (
    agenda: Omit<MeetingAgenda, 'cycleInstanceId'>,
    language: ContentLanguage,
): AgendaProjection => {
    const source: AgendaBilingualSource = {
        openingStatement: agenda.openingStatement ?? {},
        itemTitles: {},
        pointTitles: {},
        pointContexts: {},
    };

    const items: AgendaItemView[] = agenda.items.map((item) => {
        source.itemTitles[item.id] = item.title ?? {};
        return {
            id: item.id,
            title: pickText(item.title, language),
            durationMinutes: item.durationMinutes,
            order: item.order,
            hasHandout: item.hasHandout,
            isAiGenerated: item.isAiGenerated,
            discussionPoints: item.discussionPoints.map((point) => {
                source.pointTitles[point.id] = point.title ?? {};
                source.pointContexts[point.id] = point.context;
                return {
                    id: point.id,
                    title: pickText(point.title, language),
                    order: point.order,
                    hasDetails: point.hasDetails,
                    context: point.context ? pickText(point.context, language) : null,
                    dateAdded: point.dateAdded,
                    response: point.response,
                    isAiGenerated: point.isAiGenerated,
                };
            }),
        };
    });

    return { openingStatement: pickText(agenda.openingStatement, language), items, source };
};

/**
 * Merges the edited (reader-language) working copy back into a bilingual update payload, preserving the
 * other language from the saved originals. Unknown ids (newly added items/points) start from empty.
 */
export const toBilingualPayload = (
    items: AgendaItemView[],
    openingStatement: string,
    language: ContentLanguage,
    source: AgendaBilingualSource,
): MeetingAgendaUpdatePayload => ({
    openingStatement: withLanguage(source.openingStatement, language, openingStatement),
    items: items.map((item, index) => ({
        id: item.id,
        title: withLanguage(source.itemTitles[item.id], language, item.title),
        durationMinutes: item.durationMinutes,
        order: index,
        isAiGenerated: item.isAiGenerated,
        discussionPoints: item.discussionPoints.map((point, pointIndex) => ({
            id: point.id,
            title: withLanguage(source.pointTitles[point.id], language, point.title),
            order: pointIndex,
            context:
                point.context === null
                    ? null
                    : withLanguage(source.pointContexts[point.id], language, point.context),
            response: point.response,
            isAiGenerated: point.isAiGenerated,
        })),
    })),
});

/** The AI-generated agenda (both languages), parsed from the SignalR result JSON. */
export interface GeneratedAgenda {
    openingStatement: LocalizedText;
    items: {
        title: LocalizedText;
        durationMinutes: number;
        discussionPoints: { title: LocalizedText; context: LocalizedText | null }[];
    }[];
}

const toLocalized = (value: unknown): LocalizedText => {
    if (typeof value === 'string') return { en: value, fr: value };
    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const result: LocalizedText = {};
        for (const key of ['en', 'fr']) {
            const text = record[key];
            if (typeof text === 'string' && text.trim()) result[key] = text;
        }
        return result;
    }
    return {};
};

const hasText = (text: LocalizedText): boolean =>
    Object.values(text).some((value) => value.trim().length > 0);

/**
 * Parses the AI agenda JSON (bilingual { en, fr } fields) delivered over SignalR into a GeneratedAgenda.
 * Returns null when the payload can't be parsed or has no usable items.
 */
export const parseGeneratedAgenda = (
    content: string | undefined | null,
): GeneratedAgenda | null => {
    if (!content) return null;
    let model: unknown;
    try {
        model = JSON.parse(content);
    } catch {
        return null;
    }
    if (!model || typeof model !== 'object') return null;

    const root = model as Record<string, unknown>;
    const rawItems = Array.isArray(root.items) ? root.items : [];

    const items = rawItems
        .map((rawItem) => {
            const itemRecord = (rawItem ?? {}) as Record<string, unknown>;
            const title = toLocalized(itemRecord.title);
            const duration =
                typeof itemRecord.durationMinutes === 'number' && itemRecord.durationMinutes > 0
                    ? Math.min(itemRecord.durationMinutes, AGENDA_ITEM_MAX_DURATION_MINUTES)
                    : AGENDA_DEFAULT_TOPIC_DURATION_MINUTES;
            const rawPoints = Array.isArray(itemRecord.discussionPoints)
                ? itemRecord.discussionPoints
                : [];
            const discussionPoints = rawPoints
                .map((rawPoint) => {
                    const pointRecord = (rawPoint ?? {}) as Record<string, unknown>;
                    const pointTitle = toLocalized(pointRecord.title);
                    const context = toLocalized(pointRecord.context);
                    return {
                        title: pointTitle,
                        context: hasText(context) ? context : null,
                    };
                })
                .filter((point) => hasText(point.title));
            return { title, durationMinutes: duration, discussionPoints };
        })
        .filter((item) => hasText(item.title));

    if (items.length === 0) return null;

    return { openingStatement: toLocalized(root.openingStatement), items };
};

/** Each language of a generated text cut to `maxLength`, which the update endpoint refuses beyond. */
const withinLength = (text: LocalizedText, maxLength: number): LocalizedText =>
    Object.fromEntries(
        Object.entries(text).map(([language, value]) => [language, value.slice(0, maxLength)]),
    );

/**
 * The generated agenda's items as update-payload items (fresh client ids, both languages, AI-flagged),
 * following `startOrder` existing items and brought within the agenda limits: the model writes freely, and
 * a payload over any of them would be refused whole.
 */
const generatedItemsToPayload = (
    generated: GeneratedAgenda,
    startOrder: number,
): MeetingAgendaUpdatePayload['items'] =>
    generated.items.slice(0, Math.max(AGENDA_MAX_ITEMS - startOrder, 0)).map((item, index) => ({
        id: crypto.randomUUID(),
        title: withinLength(item.title, AGENDA_ITEM_TITLE_MAX_LENGTH),
        durationMinutes: item.durationMinutes,
        order: startOrder + index,
        isAiGenerated: true,
        discussionPoints: item.discussionPoints
            .slice(0, AGENDA_MAX_DISCUSSION_POINTS_PER_ITEM)
            .map((point, pointIndex) => ({
                id: crypto.randomUUID(),
                title: withinLength(point.title, AGENDA_DISCUSSION_POINT_TITLE_MAX_LENGTH),
                order: pointIndex,
                context: point.context && withinLength(point.context, AGENDA_CONTEXT_MAX_LENGTH),
                response: null,
                isAiGenerated: true,
            })),
    }));

/**
 * Builds the update payload from a generated agenda and the manager's integration choice:
 * - 'replace' → the generated opening statement + items only.
 * - 'add' → the existing items (edits preserved bilingually) followed by the generated items, as many as
 *   the agenda still has room for; the existing opening statement is kept.
 */
export const mergeGeneratedAgenda = (
    mode: 'add' | 'replace',
    generated: GeneratedAgenda,
    current: AgendaProjection,
    language: ContentLanguage,
): MeetingAgendaUpdatePayload => {
    if (mode === 'replace') {
        return {
            openingStatement: withinLength(
                generated.openingStatement,
                AGENDA_OPENING_STATEMENT_MAX_LENGTH,
            ),
            items: generatedItemsToPayload(generated, 0),
        };
    }

    const existing = toBilingualPayload(
        current.items,
        current.openingStatement,
        language,
        current.source,
    );
    return {
        openingStatement: existing.openingStatement,
        items: [...existing.items, ...generatedItemsToPayload(generated, existing.items.length)],
    };
};
